import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { motion, AnimatePresence } from "framer-motion";
import { useState, memo } from "react";
import { cn } from "@/lib/utils";
import { 
  Play, 
  Lock, 
  CheckCircle2, 
  ChevronDown, 
  ArrowRight, 
  ArrowLeft,
  BookOpen,
  Clock,
  ShieldCheck,
  Star,
  Tv,
  Users,
  MessageCircle,
  Smartphone,
  Info,
  HelpCircle,
  Plus,
  X,
  Maximize2
} from "lucide-react";
import { WhatsAppFloating } from "@/components/site/WhatsAppFloating";
import { ConsultantCTA } from "@/components/site/ConsultantCTA";

interface Lesson {
  id: string;
  title: string;
  youtubeId?: string;
  imageUrl?: string;
  description?: string;
  duration?: string;
  isLocked?: boolean;
}

interface Module {
  id: string;
  title: string;
  lessons: Lesson[];
  isLocked?: boolean;
}

const COURSE_MODULES: Module[] = [
  {
    id: "m1",
    title: "Módulo Único – Treinamento Completo",
    lessons: [
      {
        id: "l1",
        title: "Bem-vindo ao Curso IPTV",
        youtubeId: "QWQVAQBTwxY",
        description: "Nesta primeira aula você conhecerá a proposta do curso, entenderá como a plataforma funciona e verá como aproveitar ao máximo o conteúdo para acelerar seus resultados.",
        duration: "05:00"
      },
      {
        id: "l2",
        title: "Como acessar meu painel IPTV Sigma",
        youtubeId: "JfP_6WyZprI",
        description: "Aprenda a realizar o primeiro acesso e navegar pelas principais funções do painel Sigma de forma profissional.",
        duration: "08:24"
      },
      {
        id: "l3",
        title: "Criando teste no Sigma",
        youtubeId: "IVcn0V_-XVw",
        description: "Passo a passo detalhado de como gerar testes para seus clientes em poucos segundos dentro do painel Sigma.",
        duration: "06:15"
      },
      {
        id: "l4",
        title: "Ativando o teste no painel Sigma",
        youtubeId: "w0pvCnlFHXI",
        description: "Veja como converter um teste em um cliente ativo e garantir que ele tenha acesso imediato aos conteúdos.",
        duration: "07:40"
      },
      {
        id: "l5",
        title: "Como nomear o teste do cliente no Sigma",
        youtubeId: "KC1ck4EMxHs",
        description: "Organização é tudo! Aprenda a identificar e gerenciar seus testes e clientes através da nomeação correta.",
        duration: "04:30"
      },
      {
        id: "l6",
        title: "Alterando plano do cliente",
        youtubeId: "fQZjOIi5Qro",
        description: "Saiba como fazer upgrade ou downgrade dos planos dos seus clientes conforme a necessidade deles.",
        duration: "05:50"
      },
      {
        id: "l7",
        title: "Como migrar o cliente",
        youtubeId: "c-pc08ki44o",
        description: "Tutorial completo de migração de clientes entre servidores ou planos de forma segura.",
        duration: "09:12"
      },
      {
        id: "l8",
        title: "Como criar revendedor",
        youtubeId: "c1DmDX0w_v0",
        description: "Expanda seu negócio criando sua própria rede de revendedores e gerencie tudo pelo Sigma.",
        duration: "12:35"
      },
      {
        id: "l9",
        title: "Como adicionar e retirar créditos do revendedor",
        youtubeId: "70SX5bk9UDI",
        description: "Controle total sobre o saldo de créditos da sua rede de revendas.",
        duration: "06:45"
      },
      {
        id: "l10",
        title: "Como baixar app por código Downloader",
        youtubeId: "lxWBA2hZf4Q",
        description: "Aprenda o método mais rápido de instalar aplicativos em TV Boxes e Fire Sticks usando o Downloader.",
        duration: "05:20"
      },
      {
        id: "l11",
        title: "Como adicionar lista no Multi Apps",
        youtubeId: "x7R-AEyxHQ0",
        description: "Configuração passo a passo de listas de reprodução no aplicativo Multi Apps.",
        duration: "08:10"
      },
      {
        id: "l12",
        title: "Como baixar app por código NTDown",
        youtubeId: "eFRetXEtOfQ",
        description: "Utilizando a alternativa NTDown para instalação rápida de aplicativos oficiais.",
        duration: "04:55"
      },
      {
        id: "l13",
        title: "Como baixar e instalar o app IBO Play",
        youtubeId: "M1rFy4Ei04g",
        description: "Tutorial de instalação e configuração do IBO Play, um dos melhores players do mercado.",
        duration: "07:15"
      },
      {
        id: "l14",
        title: "Como baixar e adicionar lista no app Vizzion",
        youtubeId: "JWv0e_GjTqM",
        description: "Configurando o aplicativo Vizzion para seus clientes com facilidade.",
        duration: "06:30"
      },
      {
        id: "l15",
        title: "Como instalar app Assisti Plus",
        youtubeId: "7-AXrJf1iSQ",
        description: "Instalação completa do aplicativo Assisti Plus para uma experiência premium.",
        duration: "05:40"
      },
      {
        id: "l16",
        title: "Conhecendo melhor as funções do painel Sigma",
        youtubeId: "nFwpc91G5OY",
        description: "Um tour completo pelas funcionalidades avançadas e segredos do painel Sigma.",
        duration: "15:20"
      },
      {
        id: "l17",
        title: "Estratégias de Vendas",
        youtubeId: "BIO07ckN6nU",
        description: "Aprenda como atrair clientes, fechar vendas e fidelizar seu público no mercado de IPTV.",
        duration: "18:45"
      },
      {
        id: "l18",
        title: "O melhor fornecedor de IPTV",
        youtubeId: "lkg-e3Ll1QQ",
        description: "Descubra por que somos a melhor escolha para sua parceria e como crescer conosco.",
        duration: "10:30"
      }
    ]
  }
];

export const Route = createFileRoute("/curso-iptv")({
  head: () => ({
    meta: [
      { title: "Curso IPTV Profissional | Treinamento IPTV Online" },
      { name: "description", content: "Aprenda IPTV através de aulas práticas e organizadas. Curso online com conteúdo atualizado e novas aulas adicionadas regularmente." },
      { name: "keywords", content: "curso iptv, treinamento iptv, aulas de iptv, curso profissional iptv, curso online iptv, aprender iptv" },
      { property: "og:title", content: "Curso IPTV Profissional | Treinamento IPTV Online" },
      { property: "og:description", content: "Plataforma de treinamento IPTV completa para iniciantes e profissionais." },
      { property: "og:image", content: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png" },
    ],
    links: [
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
      {
        rel: "apple-touch-icon",
        href: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      },
    ],
  }),
  component: CoursePage,
});

function CoursePage() {
  const [activeModuleId, setActiveModuleId] = useState<string>("m1");
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [completedLessons] = useState<Set<string>>(new Set());

  const whatsappUrl = `https://wa.me/5583986147064?text=${encodeURIComponent("Olá! Vim da página do Curso IPTV e gostaria de saber mais sobre a revenda.")}`;

  const handleLessonSelect = (lesson: Lesson, moduleId: string) => {
    if (lesson.isLocked) return;
    setActiveLesson(lesson);
    setActiveModuleId(moduleId);
    setIsMaximized(false);
  };


  const closeLesson = () => {
    setActiveLesson(null);
    setIsMaximized(false);
  };

  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Curso IPTV", href: "/curso-iptv" }
  ];

  return (
    <Layout>
      {/* Schema FAQ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "As aulas ficam gravadas?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sim." }
            },
            {
              "@type": "Question",
              "name": "Receberei novas aulas?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sim. O curso será atualizado continuamente." }
            },
            {
              "@type": "Question",
              "name": "Posso assistir pelo celular?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sim." }
            },
            {
              "@type": "Question",
              "name": "O acesso é online?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sim." }
            }
          ]
        })}
      </script>

      <main className="pt-32 pb-24 bg-[#0a0c10]">
        <div className="max-w-7xl mx-auto px-6">
          <Breadcrumbs items={breadcrumbs} />

          {/* Header Section */}
          <section className="mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-electric/10 border border-electric/20 text-electric px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                  Treinamento Exclusivo
                </span>
                <span className="bg-white/5 text-white/40 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                  EAD Profissional
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter italic uppercase mb-6 leading-[1.1]">
                CURSO IPTV <span className="text-electric">PROFISSIONAL</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed italic mb-8">
                Aprenda passo a passo como construir, administrar e expandir seu negócio IPTV através de aulas práticas e atualizadas.
              </p>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="relative rounded-[2.5rem] overflow-hidden border border-white/5 shadow-2xl bg-white/[0.02]"
              >
                <img 
                  src="https://wogunbzijppmeuleitjq.supabase.co/storage/v1/object/public/temp-images/d1e378d3-78af-4408-b1e6-2cf505fe917e.png" 
                  alt="Banner Curso IPTV Profissional" 
                  className="w-full h-auto object-cover"
                />
              </motion.div>
            </motion.div>
          </section>

          {/* WhatsApp Call to Action - Before Course Area */}
          <section className="mb-12">
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#25D366]/10 border border-[#25D366]/20 p-6 rounded-[2rem] flex flex-col md:flex-row items-center justify-between gap-6 hover:bg-[#25D366]/20 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center text-white shadow-[0_0_20px_rgba(37,211,102,0.4)]">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-black uppercase tracking-tighter italic">Solicite seu Painel Agora</h3>
                  <p className="text-sm text-muted-foreground italic">Comece a trabalhar conosco e transforme seu conhecimento em lucro real.</p>
                </div>
              </div>
              <div className="bg-[#25D366] text-white px-8 py-3 rounded-full font-black text-[10px] uppercase tracking-[0.2em] group-hover:scale-105 transition-transform">
                Falar com Consultor
              </div>
            </a>
          </section>

          {/* Main Course Area */}
          <div className="mb-24 relative">
            
            {/* Full Width Menu - Since it's a single module now */}
            <div className="bg-white/[0.02] border border-white/5 rounded-[2.5rem] overflow-hidden z-20">
              <div className="p-8 border-b border-white/5 bg-white/[0.01] flex flex-col md:flex-row md:items-center justify-between gap-6">
                <h3 className="font-black text-xl uppercase tracking-tighter italic flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-electric" />
                  {COURSE_MODULES[0].title}
                </h3>
                <div className="flex flex-col gap-2 min-w-[200px]">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Progresso do Curso</span>
                    <span className="text-[10px] font-black text-electric">100%</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      className="h-full bg-electric shadow-glow-sm"
                    />
                  </div>
                </div>
              </div>
              
              <div className="divide-y divide-white/5">
                {COURSE_MODULES[0].lessons.map((lesson) => (
                  <div key={lesson.id} className="flex flex-col border-b border-white/5 last:border-0">
                    <button
                      onClick={() => activeLesson?.id === lesson.id ? closeLesson() : handleLessonSelect(lesson, "m1")}
                      className={cn(
                        "w-full px-6 md:px-8 py-5 md:py-6 flex items-center gap-4 md:gap-6 transition-all hover:bg-white/5 group text-left",
                        activeLesson?.id === lesson.id ? "bg-white/[0.03] text-electric" : "text-muted-foreground"
                      )}
                    >
                      <div className={cn(
                        "w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all",
                        activeLesson?.id === lesson.id ? "bg-electric text-white shadow-glow-sm" : "bg-white/5 group-hover:bg-white/10"
                      )}>
                        {activeLesson?.id === lesson.id ? <Play className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 opacity-40" />}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-black uppercase tracking-widest block mb-1">{lesson.title}</span>
                        {lesson.duration && <span className="text-[10px] opacity-40 font-black uppercase tracking-widest flex items-center gap-1.5"><Clock className="w-3 h-3" /> {lesson.duration}</span>}
                      </div>
                      <ChevronDown className={cn("w-5 h-5 transition-transform opacity-30", activeLesson?.id === lesson.id && "rotate-180")} />
                    </button>

                    <AnimatePresence mode="wait">
                      {activeLesson?.id === lesson.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "circOut" }}
                          className="overflow-hidden bg-black/40 border-t border-white/5"
                        >
                          <div className={cn(
                            "p-4 md:p-8 flex flex-col gap-6",
                            isMaximized ? "fixed inset-0 z-[200] bg-black p-0 rounded-none overflow-y-auto overscroll-none" : "relative"
                          )}>
                            {/* Video Controls (Maximized) */}
                            {isMaximized && (
                              <div className="absolute top-6 right-6 z-[210] flex items-center gap-3">
                                <button 
                                  onClick={() => setIsMaximized(false)}
                                  className="p-3 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white hover:bg-electric transition-all"
                                >
                                  <Maximize2 className="w-5 h-5" />
                                </button>
                                <button 
                                  onClick={closeLesson}
                                  className="p-3 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white hover:bg-red-500 transition-all"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            )}

                            {/* Video Container */}
                            <div className={cn(
                              "aspect-video relative bg-black overflow-hidden rounded-[1.5rem] border border-white/5 shadow-2xl w-full mx-auto max-w-full",

                              isMaximized ? "w-full h-full rounded-none border-0" : "w-full"
                            )}>
                              {lesson.youtubeId ? (
                                <iframe
                                  src={`https://www.youtube.com/embed/${lesson.youtubeId}?rel=0&modestbranding=1&autoplay=1`}
                                  className="absolute inset-0 w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  title={lesson.title}
                                />
                              ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center bg-gradient-to-br from-[#12141a] to-black">
                                  <Lock className="w-12 h-12 text-white/10 mb-4" />
                                  <h3 className="text-xl font-black uppercase tracking-tighter italic">Conteúdo em Breve</h3>
                                </div>
                              )}
                            </div>

                            {/* Info & Maximizer (Not Maximized) */}
                            {!isMaximized && (
                              <div className="w-full flex flex-col md:flex-row items-start justify-between gap-8 pb-4">

                                <div className="flex-1">
                                  <h4 className="text-xl font-black uppercase tracking-tighter italic mb-3 flex items-center gap-2">
                                    <Info className="w-5 h-5 text-electric" />
                                    Sobre esta aula
                                  </h4>
                                  <p className="text-muted-foreground italic leading-relaxed text-sm">
                                    {lesson.description || "Nesta aula você aprenderá detalhes importantes sobre este tópico do curso IPTV Profissional."}
                                  </p>
                                  {lesson.imageUrl && (
                                    <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 max-w-2xl">
                                      <img 
                                        src={lesson.imageUrl} 
                                        alt={`Auxiliar ${lesson.title}`} 
                                        className="w-full h-auto object-cover"
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-3">
                                  <button 
                                    onClick={() => setIsMaximized(true)}
                                    className="px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-electric hover:border-electric transition-all"
                                  >
                                    <Maximize2 className="w-4 h-4" />
                                    Tela Cheia
                                  </button>
                                  <button 
                                    onClick={closeLesson}
                                    className="px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-red-500/20 hover:text-red-500 hover:border-red-500/50 transition-all"
                                  >
                                    <X className="w-4 h-4" />
                                    Fechar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

            {/* Suporte CTA */}
            <div className="mt-12 p-8 md:p-10 rounded-[2.5rem] bg-electric/10 border border-electric/20 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-electric/20 blur-[100px] rounded-full -mr-32 -mt-32" />
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tighter italic mb-2">Dúvidas sobre o curso ou revenda?</h3>
                  <p className="text-muted-foreground text-sm max-w-md italic leading-relaxed">Fale agora com nosso suporte especializado e acelere seus resultados no mercado IPTV.</p>
                </div>
                <a 
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white px-8 py-4 rounded-full font-black text-[11px] uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(37,211,102,0.3)] hover:scale-105 transition-all flex items-center gap-3 whitespace-nowrap w-full md:w-auto justify-center"
                >
                  Suporte no WhatsApp
                  <MessageCircle className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>


          {/* About Section */}
          <section className="mb-24 grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-electric text-[10px] font-black uppercase tracking-[0.3em] mb-4 block">Metodologia Exclusiva</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter italic uppercase mb-8 leading-tight">
                Sobre Este <span className="text-electric">Curso</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 italic">
                Este treinamento foi desenvolvido para ensinar de forma prática os principais conceitos do mercado IPTV. Novos conteúdos serão adicionados periodicamente para manter o treinamento atualizado e cada vez mais completo.
              </p>
              <div className="space-y-4">
                {[
                  "Aulas gravadas em alta definição",
                  "Conteúdo focado em resultados reais",
                  "Plataforma disponível 24h por dia",
                  "Suporte técnico especializado"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-electric flex-shrink-0" />
                    <span className="font-bold text-sm uppercase tracking-widest">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative p-12 rounded-[3rem] bg-white/[0.02] border border-white/5 overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-electric/10 blur-[80px] rounded-full -mr-32 -mt-32" />
              <div className="relative z-10 grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-black text-electric mb-2 italic tracking-tighter">100%</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Online</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-electric mb-2 italic tracking-tighter">+50</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Aulas Futuras</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-electric mb-2 italic tracking-tighter">4K</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Qualidade</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-electric mb-2 italic tracking-tighter">VIP</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Suporte</div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Benefits Grid */}
          <section className="mb-24">
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter italic uppercase mb-12 text-center">
              Benefícios da <span className="text-electric">Plataforma</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Acesso Online", desc: "Estude de qualquer lugar, a qualquer hora.", icon: <ShieldCheck /> },
                { title: "Atualizações Futuras", desc: "Novas aulas adicionadas regularmente sem custo extra.", icon: <Star /> },
                { title: "Conteúdo Organizado", desc: "Trilha de aprendizado lógica para acelerar seus ganhos.", icon: <BookOpen /> },
                { title: "Aprendizado Progressivo", desc: "Do básico ao avançado com passos simples.", icon: <Tv /> },
                { title: "Plataforma Intuitiva", desc: "Interface moderna e fácil de navegar.", icon: <Users /> },
                { title: "Material em Expansão", desc: "Curso que cresce junto com o mercado.", icon: <Plus /> },
              ].map((item, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
                  <div className="w-12 h-12 rounded-2xl bg-electric/10 flex items-center justify-center text-electric mb-6 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 italic uppercase tracking-tighter">{item.title}</h3>
                  <p className="text-sm text-muted-foreground italic leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-4xl mx-auto mb-24">
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter italic uppercase mb-12 text-center">
              Dúvidas <span className="text-electric">Frequentes</span>
            </h2>
            <div className="space-y-4">
              {[
                { q: "As aulas ficam gravadas?", a: "Sim, você pode assistir quantas vezes quiser, de qualquer dispositivo." },
                { q: "Receberei novas aulas?", a: "Sim. O curso é uma plataforma viva e será atualizado continuamente com as novidades do mercado." },
                { q: "Posso assistir pelo celular?", a: "Sim, nossa plataforma é totalmente responsiva e otimizada para smartphones." },
                { q: "O acesso é online?", a: "Sim, todo o conteúdo é 100% digital e o acesso é imediato após a inscrição." }
              ].map((faq, i) => (
                <CourseFAQ key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </section>

          <ConsultantCTA />
        </div>
      </main>
      <WhatsAppFloating />
    </Layout>
  );
}

const ModuleAccordion = memo(({ 
  module, 
  activeModuleId, 
  activeLessonId, 
  onLessonSelect,
  isOpen,
  onToggle
}: { 
  module: Module; 
  activeModuleId: string; 
  activeLessonId: string;
  onLessonSelect: (lesson: Lesson, moduleId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  return (
    <div className="border-b border-white/5 last:border-0">
      <button 
        onClick={onToggle}
        className={cn(
          "w-full p-6 flex items-center justify-between text-left transition-colors",
          isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.01]"
        )}
      >
        <div className="flex items-center gap-3">
          {module.isLocked ? (
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white/20" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-lg bg-electric/10 flex items-center justify-center">
              <Play className="w-4 h-4 text-electric fill-electric" />
            </div>
          )}
          <span className={cn(
            "text-xs font-bold uppercase tracking-widest",
            module.isLocked ? "text-white/20" : "text-white"
          )}>
            {module.title}
          </span>
        </div>
        <ChevronDown className={cn("w-4 h-4 transition-transform opacity-30", isOpen && "rotate-180")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-black/40"
          >
            {module.lessons.length > 0 ? (
              <div className="py-2">
                {module.lessons.map((lesson) => (
                  <button
                    key={lesson.id}
                    onClick={() => onLessonSelect(lesson, module.id)}
                    className={cn(
                      "w-full px-8 py-4 flex items-center gap-4 transition-all hover:bg-white/5 group border-b border-white/[0.03] last:border-0",
                      activeLessonId === lesson.id ? "bg-white/5 text-electric" : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0",
                      activeLessonId === lesson.id ? "bg-electric shadow-glow-sm" : "bg-white/10 group-hover:bg-white/30"
                    )} />
                    <span className="text-[11px] font-bold uppercase tracking-widest flex-1 text-left">{lesson.title}</span>
                    {lesson.duration && <span className="text-[9px] opacity-40 font-black">{lesson.duration}</span>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20">Novas aulas serão adicionadas em breve.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

ModuleAccordion.displayName = "ModuleAccordion";

function CourseFAQ({ question, answer }: { question: string, answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between text-left group"
      >
        <span className="font-bold uppercase tracking-widest text-sm group-hover:text-electric transition-colors">{question}</span>
        <div className={cn("p-2 rounded-lg bg-white/5 transition-transform", isOpen && "rotate-180")}>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 text-muted-foreground italic text-sm leading-relaxed border-t border-white/5 mt-0">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
